import { useStore } from '@tanstack/react-form'
import { useBlocker, useNavigate, useRouter } from '@tanstack/react-router'
import { AlertCircleIcon } from 'lucide-react'
import {
	createContext,
	PropsWithChildren,
	RefObject,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState
} from 'react'

import { Api } from '@/api/api'
import { useApiContext } from '@/api/react-query'
import { Button } from '@/components/base/button'
import { Drawer } from '@/components/base/drawer'
import { FocusModal } from '@/components/base/focus-modal'
import { LoadingSpinner } from '@/components/base/loading-spinner'
import { Prompt } from '@/components/base/prompt'
import { Text } from '@/components/base/text'
import { useAppForm } from '@/components/form/form'
import { useI18n } from '@/i18n/i18n-react'
import { clx } from '@/utils/clx'
import { useAsyncEffect } from '@/utils/hooks'

type RouteModalContextType = {
	shouldByPassNavigationBlocker: RefObject<boolean>
	handleSuccess: (path?: string) => void
	close: () => void
}

export const RouteModalContext = createContext<RouteModalContextType>(undefined!)

export const useRouteModal = () => {
	const context = useContext(RouteModalContext)

	if (!context) {
		throw new Error('useRouteModal must be used within a RouteModal')
	}

	return context
}

export type ChildrenWithPossibleLoader<TData> =
	| { requiresData?: false; children: React.ReactNode }
	| { requiresData: true; children: (data: TData) => React.ReactNode; loader: (api: Api) => Promise<TData> }

export const RouteModal = <TData,>({
	prev = '..',
	asDrawer = false,
	...childrenRelatedProps
}: {
	prev?: string
	asDrawer?: boolean
} & ChildrenWithPossibleLoader<TData>) => {
	const navigate = useNavigate()
	const router = useRouter()
	const [open, setOpen] = useState(false)

	/**
	 * The history index this route's entry had when the modal mounted. A route modal is
	 * always reached by pushing a new entry on top of its parent (via a `Link`), which means
	 * closing it with a plain `replace` leaves that pushed entry in the stack -- it just
	 * overwrites the top with the parent's URL instead of popping it. Across several
	 * open/close cycles (e.g. media then prices) those leftover entries stack up, each one
	 * requiring an extra Back press to get past. Popping with `history.back()` instead
	 * collapses the stack correctly, so we only need to detect when that's safe to do: the
	 * entry must have actually been pushed (index > 0, i.e. not a direct/deep link landing
	 * here as the first history entry) and nothing else must have navigated since we opened.
	 */
	const openedAtIndex = useRef(router.history.location.state.__TSR_index)

	/**
	 * Open the modal when the component mounts. This
	 * ensures that the entry animation is played.
	 */
	useEffect(() => {
		setOpen(true)

		return () => {
			setOpen(false)
		}
	}, [])

	const closeTo = useCallback(
		(to: string) => {
			const canPopHistory =
				to === prev &&
				openedAtIndex.current > 0 &&
				router.history.location.state.__TSR_index === openedAtIndex.current
			if (canPopHistory) {
				router.history.back()
				return
			}
			navigate({ to, replace: true })
		},
		[navigate, prev, router]
	)

	const handleOpenChange = (open: boolean) => {
		if (!open) {
			document.body.style.pointerEvents = 'auto'
			closeTo(prev)
			return
		}

		setOpen(open)
	}

	const shouldByPassNavigationBlocker = useRef(false)
	const handleSuccess = useCallback(
		(path?: string) => {
			shouldByPassNavigationBlocker.current = true
			closeTo(path || prev)
		},
		[closeTo, prev]
	)
	const close = useCallback(() => {
		setOpen(false)
	}, [])

	const contextValue = useMemo(
		() => ({ handleSuccess, shouldByPassNavigationBlocker, close }),
		[handleSuccess, close]
	)

	const Component = asDrawer ? Drawer : FocusModal
	return (
		<Component open={open} onOpenChange={handleOpenChange}>
			<RouteModalContext.Provider value={contextValue}>
				<ContentWrapper asDrawer={asDrawer}>
					{childrenRelatedProps.requiresData ? (
						<ContentWithLoader
							children={childrenRelatedProps.children}
							loader={childrenRelatedProps.loader}
						/>
					) : (
						childrenRelatedProps.children
					)}
				</ContentWrapper>
			</RouteModalContext.Provider>
		</Component>
	)
}

function ContentWrapper({ asDrawer, children }: PropsWithChildren<{ asDrawer?: boolean }>) {
	if (asDrawer) {
		return (
			<Drawer.Content aria-describedby={undefined} className={clx('!inset-y-5 !right-5 !bg-ui-bg-disabled')}>
				{children}
			</Drawer.Content>
		)
	}
	return <FocusModal.Content>{children}</FocusModal.Content>
}

function ContentWithLoader<TData>({
	children,
	loader
}: {
	children: (data: TData) => React.ReactNode
	loader: (api: Api) => Promise<TData>
}) {
	const { L } = useI18n()
	const { close } = useRouteModal()

	const [data, setData] = useState<TData | null>(null)
	const [isLoading, setIsLoading] = useState(false)
	const [error, setError] = useState<Error | undefined>(undefined)

	const { api } = useApiContext()

	useAsyncEffect(async () => {
		try {
			setIsLoading(true)
			const data = await loader(api)
			setData(data)
		} catch (e) {
			setError(e as Error)
		} finally {
			setIsLoading(false)
		}
	}, [])

	if (isLoading) {
		return (
			<div className='flex size-full flex-col items-center justify-center gap-4'>
				<LoadingSpinner />
			</div>
		)
	}

	if (error) {
		return (
			<div className='flex size-full flex-col items-center justify-center gap-4'>
				<AlertCircleIcon className='size-10 text-ui-danger' />
				<Text className='text-ui-danger'>{error.message}</Text>
				<Button onClick={close}>{L.General.Close()}</Button>
			</div>
		)
	}

	if (!data) {
		return null
	}

	return children(data)
}

export const RouteModalFormBlocker = ({ form }: { form: ReturnType<typeof useAppForm> }) => {
	const { L } = useI18n()

	const { shouldByPassNavigationBlocker } = useRouteModal()

	// Block on an actual value comparison (`isDefaultValue` deep-compares current
	// values against the form's defaults) rather than the sticky `isDirty` flag,
	// which TanStack Form sets on any programmatic `setFieldValue`/field init and
	// never clears — causing false "Unsaved changes" prompts.
	const isDefaultValue = useStore(form.store, state => state.isDefaultValue)
	const isSubmitted = useStore(form.store, state => state.isSubmitted)
	const blocker = useBlocker({
		shouldBlockFn: () => !shouldByPassNavigationBlocker.current && !isDefaultValue && !isSubmitted,
		withResolver: true
	})

	const handleCancel = () => {
		blocker?.reset?.()
	}

	const handleContinue = () => {
		blocker?.proceed?.()
	}

	return (
		<Prompt open={blocker.status === 'blocked'} variant='confirmation'>
			<Prompt.Content>
				<Prompt.Header>
					<Prompt.Title>{L.General.UnsavedChangesTitle()}</Prompt.Title>
					<Prompt.Description>{L.General.UnsavedChangesDescription()}</Prompt.Description>
				</Prompt.Header>
				<Prompt.Footer>
					<Prompt.Cancel onClick={handleCancel} type='button'>
						{L.General.Cancel()}
					</Prompt.Cancel>
					<Prompt.Action onClick={handleContinue} type='button'>
						{L.General.Continue()}
					</Prompt.Action>
				</Prompt.Footer>
			</Prompt.Content>
		</Prompt>
	)
}

/**
 * Standalone unsaved-changes guard for full-page editors that are not TanStack-Form-backed
 * RouteModals. Pass a plain `dirty` flag; blocks in-app navigation (via the router) and warns on
 * browser reload / tab close (via `beforeunload`) while dirty.
 */
export const UnsavedChangesBlocker = ({ dirty }: { dirty: boolean }) => {
	const { L } = useI18n()

	const blocker = useBlocker({
		shouldBlockFn: () => dirty,
		enableBeforeUnload: () => dirty,
		withResolver: true
	})

	return (
		<Prompt open={blocker.status === 'blocked'} variant='confirmation'>
			<Prompt.Content>
				<Prompt.Header>
					<Prompt.Title>{L.General.UnsavedChangesTitle()}</Prompt.Title>
					<Prompt.Description>{L.General.UnsavedChangesDescription()}</Prompt.Description>
				</Prompt.Header>
				<Prompt.Footer>
					<Prompt.Cancel onClick={() => blocker?.reset?.()} type='button'>
						{L.General.Cancel()}
					</Prompt.Cancel>
					<Prompt.Action onClick={() => blocker?.proceed?.()} type='button'>
						{L.General.Continue()}
					</Prompt.Action>
				</Prompt.Footer>
			</Prompt.Content>
		</Prompt>
	)
}
